/**
 * "Connect your Expo account" / "Connect Codemagic": the steps and the link the API gives (`help`), and a password
 * field to paste the token once (PUT /me/device-builds/connections/:provider). The token never comes back: after
 * saving, only "Connected as …". Where this Flow has no secret store (`available` false) it says why and nothing else.
 */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Alert, Button, Input } from 'antd';
import { connectProvider } from '../../services/DeviceBuildsService';
import { CONNECTION_NAME, CONNECTION_TITLE } from './deviceBuildsText';

export default function ConnectProviderPanel({ provider, info = null, available = true, reason = null, onConnected }) {
  const [token, setToken] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const name = CONNECTION_NAME[provider];
  const help = info?.help || null;

  const save = async() => {
    setSaving(true);
    setError(null);
    try {
      const answer = await connectProvider(provider, token.trim());
      setToken('');
      onConnected(answer?.provider || { connected: true, account: null });
    } catch (err) {
      setError(err?.message || `${name} refused the token.`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="devb-panel" data-testid={`devb-connect-${provider}`}>
      <h4>{CONNECTION_TITLE[provider]}</h4>
      {!available ? (
        <p className="devb-muted" data-testid="devb-connect-unavailable">{reason || 'This Flow cannot keep tokens (it has no secret store). Ask your Flow admin.'}</p>
      ) : (
        <>
          <p className="devb-muted">
            {name} builds the app on its servers with your account. Paste an access token once: Flow keeps it in its secret
            store and never shows it again.
          </p>
          {help?.steps?.length > 0 && (
            <ol className="devb-steps" data-testid="devb-connect-steps">
              {help.steps.map((step) => <li key={step}>{step}</li>)}
            </ol>
          )}
          {help?.url && <p><a data-testid="devb-connect-link" href={help.url} rel="noopener noreferrer" target="_blank">Open {name} to create a token ↗</a></p>}
          <div className="devb-row">
            <Input.Password
              aria-label={`${name} token`}
              autoComplete="off"
              data-testid="devb-connect-token"
              onChange={(event) => setToken(event.target.value)}
              onPressEnter={() => { if (token.trim() && !saving) { save(); } }}
              placeholder={`Paste your ${name} token`}
              value={token}
            />
            <Button data-testid="devb-connect-save" disabled={!token.trim()} loading={saving} onClick={save} type="primary">Connect</Button>
          </div>
          {error && <Alert className="devb-alert" data-testid="devb-connect-error" showIcon title={error} type="error" />}
        </>
      )}
    </section>
  );
}

ConnectProviderPanel.propTypes = {
  // Whether this Flow can keep tokens (GET connections › available) and why not.
  available: PropTypes.bool,
  // GET connections › providers[provider]: `{ connected, account, help: { url, steps } }`.
  info: PropTypes.object,
  // ({ connected, account }) => after the token was saved.
  onConnected: PropTypes.func.isRequired,
  provider: PropTypes.oneOf(['expo', 'codemagic']).isRequired,
  reason: PropTypes.string,
};
